import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Bug, Pencil, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '~/services/supabase';
import { fetchFormActivity, fetchEnabledIntegrations, softDeleteSubmissions } from '~/services/analytics';
import { useDateFilter } from '~/hooks/useDateFilter';
import { DateFilterBar } from '~/components/DateFilterBar';
import { FormActivityTable } from '~/components/FormActivityTable';
import { ShareModal } from '~/components/ShareModal';
import { FormIntegrations } from '~/components/FormIntegrations';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/lib/utils';
import type { DateFilterPreset, IFormWithCount, IFormActivityRow } from '~/types';

export const FormDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { preset, setPreset, dateRange } = useDateFilter('all');

  const [form, setForm] = useState<IFormWithCount | null>(null);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);

  const [rows, setRows] = useState<IFormActivityRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [enabledIntegrations, setEnabledIntegrations] = useState<string[]>([]);

  // Load form metadata
  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data, error } = await supabase.from('forms').select('*, form_submissions(count)').eq('id', id).single();
      if (error || !data) {
        console.error('Error loading form:', error);
        setLoading(false);
        return;
      }
      setForm({
        ...data,
        submissionCount: (data.form_submissions as Array<{ count: number }>)?.[0]?.count ?? 0,
      });
      setLoading(false);
    })();
  }, [id]);

  // Load enabled integrations for this form
  useEffect(() => {
    if (!id) return;
    fetchEnabledIntegrations(id).then(setEnabledIntegrations);
  }, [id]);

  // Load submissions
  useEffect(() => {
    if (!id) return;
    (async () => {
      const result = await fetchFormActivity(dateRange, id, page);
      setRows(result.rows);
      setTotal(result.total);
    })();
  }, [id, dateRange, page]);

  const handleDateFilterChange = (value: DateFilterPreset) => {
    setPreset(value);
    setPage(1);
  };

  const handleDeleteSubmissions = async (ids: string[]) => {
    await softDeleteSubmissions(ids);
    toast.success(`Deleted ${ids.length} submission${ids.length !== 1 ? 's' : ''}`);
    const result = await fetchFormActivity(dateRange, id!, page);
    setRows(result.rows);
    setTotal(result.total);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Form not found.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{form.name}</h1>
          <Badge
            variant="secondary"
            className={cn(
              form.status === 'published' && 'bg-green-100 text-green-800',
              form.status === 'draft' && 'bg-yellow-100 text-yellow-800',
              form.status === 'archived' && 'bg-gray-100 text-gray-800',
            )}
          >
            {form.status}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {form.submissionCount} submission{form.submissionCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" asChild>
                  <Link to={`/forms/${form.id}/logs`}>
                    <Bug className="h-4 w-4" />
                  </Link>
                </Button>
              </TooltipTrigger>
              <TooltipContent>View Logs</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button variant="outline" onClick={() => setShowShareModal(true)}>
            <Share2 className="mr-2 h-4 w-4" />
            Share
          </Button>
          <Button asChild>
            <Link to={`/forms/${form.id}/edit`}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit Form
            </Link>
          </Button>
        </div>
      </div>

      {/* Date filter */}
      <DateFilterBar value={preset} onChange={handleDateFilterChange} />

      {/* Integrations */}
      <FormIntegrations formId={form.id} formSchema={form.json_schema} />

      {/* Submissions table */}
      <FormActivityTable
        rows={rows}
        total={total}
        page={page}
        onPageChange={setPage}
        selectedForm={form}
        onDelete={handleDeleteSubmissions}
        enabledIntegrations={enabledIntegrations}
      />

      {showShareModal && <ShareModal formId={form.id} onClose={() => setShowShareModal(false)} />}
    </div>
  );
};
